import { API_TO_MARKET_SEGMENT_MAPPER, EXCHANGE_LABEL_MAPPER, MARKET_SEGEMENT_MAPPER, MARKET_SEGMENT_LABELS, SCRIP_INSTRUMENTS, SEGMENT_ID_MAPPER } from "../../../constants/equity/common";
import commonBaseService from "../../commonBaseService";
import orderScripService from "../orderForm/orderScripService";
import STOCKS_PORTFOLIO_LANDING from "../../../strings/equity/stocksPortfolio/stocksPortfolioLanding";
import POSITION_DETAILS from "../../../strings/equity/stocksPortfolio/positionDetails";
import stocksPortfolioPnlService from "./stocksPortfolioPnlService";
import equityBaseService from "../equityBaseService";
import { ltpFeedService } from "../ltpFeedService";
import stocksPortfolioService from './stocksPortfolioService';
import { format } from 'date-fns';

const constructHoldingsParamsForBatchRequest = (holdings) => {
	const socketParamsList = [];
	holdings.forEach((item) => {
		const { nse_token, bse_token, nse_segment_id, bse_segment_id } = item;

		const isNseTokenAndIdAvailable =
			!commonBaseService.isEmptyOrUndefined(nse_token) || !commonBaseService.isEmptyOrUndefined(nse_segment_id);
		const isBseTokenAndIdAvailable =
			!commonBaseService.isEmptyOrUndefined(bse_token) || !commonBaseService.isEmptyOrUndefined(bse_segment_id);

		if (!isNseTokenAndIdAvailable && !isBseTokenAndIdAvailable) {
			return;
		}

		let socketParam = {};
		if (isNseTokenAndIdAvailable) {
			socketParam = {
				nToken: nse_token,
				market_segment: API_TO_MARKET_SEGMENT_MAPPER[nse_segment_id],
			};
		} else if (isBseTokenAndIdAvailable) {
			socketParam = {
				nToken: bse_token,
				market_segment: API_TO_MARKET_SEGMENT_MAPPER[bse_segment_id],
			};
		}

		socketParamsList.push(socketParam);
	});

	return socketParamsList;
};

const constructPositionsParamsForBatchRequest = (positions) => {
	const socketParamsList = [];
	positions.forEach((scrip) => {
		const { scrip_token, exchange } = scrip;
		const isScripTokenAndMarketSegmentAvailable = scrip_token && exchange;
		if (isScripTokenAndMarketSegmentAvailable) {
			const socketParam = {
				nToken: scrip_token,
				market_segment: SEGMENT_ID_MAPPER[scrip.exchange],
			};
			socketParamsList.push(socketParam);
		}
	});
	return socketParamsList;
};

const getFormattedHoldingsViewData = ({
	invested,
	current,
	totalPnl,
	totalPnlPercentage,
	todaysPnl,
	totalTodaysPnlPerc,
}) => {
	const holdingsSummaryViewData = {
		invested: {
			text: commonBaseService.formatNumber(invested, {
				addCurrencySymbol: true,
				decimalPlaces: 0,
			}),
		},
		current: {
			text: commonBaseService.formatNumber(current, {
				addCurrencySymbol: true,
				decimalPlaces: 0,
			}),
		},
		totalPnl: {
			text: commonBaseService.formatNumber(totalPnl, {
				addSign: true,
				addCurrencySymbol: true,
				decimalPlaces: 0,
			}),
			color: equityBaseService.getEquityPriceColorBasedOnValue(totalPnlPercentage),
		},
		totalPnlPercentage: {
			text: `(${commonBaseService.formatNumber(totalPnlPercentage, {
				formatAsPercentage: true,
				retainNegativeSign: false,
			})})`,
			color: equityBaseService.getEquityPriceColorBasedOnValue(totalPnlPercentage),
		},
		todaysPnl: {
			text: commonBaseService.formatNumber(todaysPnl, {
				addSign: true,
				addCurrencySymbol: true,
				decimalPlaces: 0,
			}),
			color: equityBaseService.getEquityPriceColorBasedOnValue(totalTodaysPnlPerc),
		},
		totalTodaysPnlPerc: {
			text: `(${commonBaseService.formatNumber(totalTodaysPnlPerc, {
				formatAsPercentage: true,
				retainNegativeSign: false,
			})})`,
			color: equityBaseService.getEquityPriceColorBasedOnValue(totalTodaysPnlPerc),
		},
	};

	return holdingsSummaryViewData;
};

const getFormattedPositionsViewData = ({ totalOverallPnl }) => {
	return {
		overAllPnl: {
			text: commonBaseService.formatNumber(totalOverallPnl, {
				addCurrencySymbol: true,
				addSign: true,
				decimalPlaces: 0,
			}),
			color: equityBaseService.getEquityPriceColorBasedOnValue(parseInt(totalOverallPnl, 10)),
		},
	};
};

const getFormattedHoldingsListItemViewData = (
	overallPnl,
	overallPnlPercentage,
	todaysPnl,
	ltp,
	netchangefromprevclose,
) => {
	const holdingListItemViewData = {
		overallPnlData: {
			text: commonBaseService.formatNumber(overallPnl, {
				addSign: true,
				decimalPlaces: 0,
			}),
			color: equityBaseService.getEquityPriceColorBasedOnValue(overallPnlPercentage),
		},
		overallPnlPercentageData: {
			text: `(${commonBaseService.formatNumber(overallPnlPercentage, {
				formatAsPercentage: true,
				retainNegativeSign: false,
			})})`,
			color: equityBaseService.getEquityPriceColorBasedOnValue(overallPnlPercentage),
		},
		todaysPnl: {
			text: commonBaseService.formatNumber(todaysPnl, {
				addSign: true,
				decimalPlaces: 0,
			}),
			color: equityBaseService.getEquityPriceColorBasedOnValue(netchangefromprevclose),
		},
		todaysPnlPercentageData: {
			text: `(${commonBaseService.formatNumber(netchangefromprevclose, {
				formatAsPercentage: true,
				retainNegativeSign: false,
			})})`,
			color: equityBaseService.getEquityPriceColorBasedOnValue(netchangefromprevclose),
		},
		ltpData: {
			text: ltp,
		},
		netChangeData: {
			text: `(${commonBaseService.formatNumber(netchangefromprevclose, {
				formatAsPercentage: true,
				addSign: true,
			})})`,
			color: equityBaseService.getEquityPriceColorBasedOnValue(netchangefromprevclose),
		},
	};

	return holdingListItemViewData;
};

const getFormattedPositionsListItemViewData = ({ overAllPnl, ltp }) => {
	return {
		pnlData: {
			text: commonBaseService.formatNumber(overAllPnl, {
				addSign: true,
				decimalPlaces: 0,
			}),
			color: equityBaseService.getEquityPriceColorBasedOnValue(parseInt(overAllPnl, 10)),
		},
		ltpData: {
			text: ltp,
		},
	};
};

const getFormattedExpiryDate = (dt) => {
	if (!dt) {
		return '';
	}

	try {
		const date = new Date(dt);
		const formattedDate = format(date, 'yyyy-MM-dd');
		return formattedDate;
	} catch (error) {
		return 'Invalid Date';
	}
};

const formatHoldingAndPositionCount = (count, isHoldingVariant) => {
	let textValue = '';

	if (isHoldingVariant) {
		textValue = count !== 1 ? STOCKS_PORTFOLIO_LANDING.SEARCH_SORT_STRIP.counter.holding.multipleText : STOCKS_PORTFOLIO_LANDING.SEARCH_SORT_STRIP.counter.holding.singleText;
	} else {
		textValue = count !== 1 ? STOCKS_PORTFOLIO_LANDING.SEARCH_SORT_STRIP.counter.position.multipleText : STOCKS_PORTFOLIO_LANDING.SEARCH_SORT_STRIP.counter.position.singleText;
	}

	return `${count} ${textValue}`;
};

export const formatHoldingDetailsData = (params = {}) => {
	const {
		dp_quantity,
		display_name,
		nse_token,
		nse_segment_id,
		bse_token,
		bse_segment_id,
		instrument_name = '',
		dp_buy_price,
	} = params;

	const scripName = display_name;
	const scripToken = nse_token || bse_token;
	const marketSegment = nse_segment_id || bse_segment_id;
	const scripSegment = MARKET_SEGEMENT_MAPPER[marketSegment];
	const investedValue = stocksPortfolioPnlService.calculateInvestedValue(dp_quantity, dp_buy_price);
	const isCurrencyScrip = orderScripService.isCurrencyDerivatesScrip(instrument_name);
	const decimalPlaces = ltpFeedService.getSupportedDecimals(instrument_name);
	const formatedInvestedValue = commonBaseService.formatNumber(investedValue, {
		addCurrencySymbol: true,
		decimalPlaces,
	});
	const scripInfo = {
		scripToken,
		scripSegment,
		scripSymbol: scripName,
		scripType: instrument_name || SCRIP_INSTRUMENTS.EQUITY,
	};
	const averagePrice = commonBaseService.getNumberInDecimalFormat(dp_buy_price, decimalPlaces);
	return {
		scripName,
		scripInfo,
		holdingScripDetails: {
			formatedInvestedValue,
			quantity: dp_quantity,
			averagePrice: averagePrice,
			isCurrencyScrip,
		},
	};
};

export const formatExchangeBottomsheetOptions = (params = {}) => {
	const { nse_token, nse_segment_id, bse_token, bse_segment_id } = params;

	const bottomSheetOptions = [];

	if (!commonBaseService.isEmptyOrUndefined(nse_token)) {
		bottomSheetOptions.push({
			label: MARKET_SEGMENT_LABELS.NSE,
			value: 'nse',
			token: nse_token,
			segment: MARKET_SEGEMENT_MAPPER[nse_segment_id],
			testIdSuffix: 'nse',
			grpId: 'selectedNSE',
		});
	}

	if (!commonBaseService.isEmptyOrUndefined(bse_token)) {
		bottomSheetOptions.push({
			label: MARKET_SEGMENT_LABELS.BSE,
			value: 'bse',
			token: bse_token,
			segment: MARKET_SEGEMENT_MAPPER[bse_segment_id],
			testIdSuffix: 'bse',
			grpId: 'selectedBSE',
		});
	}

	return bottomSheetOptions;
};

export const formatRealtimePnlValues = (feedValues = {}) => {
	let {
		netchangefromprevclose,
		overallPnl,
		todaysPnl,
		overallPnlPercentage,
		currentValue,
		last_trade_price,
		one_day_change,
	} = feedValues;
	const { divider } = feedValues;
	const overallPnlColor = equityBaseService.getEquityPriceColorBasedOnValue(overallPnl);
	const todaysPnlColor = equityBaseService.getEquityPriceColorBasedOnValue(todaysPnl);
	const oneDayChangeColor = equityBaseService.getEquityPriceColorBasedOnValue(one_day_change);
	const netChangeColor = equityBaseService.getEquityPriceColorBasedOnValue(netchangefromprevclose);
	const decimalPlaces = 2;
	currentValue = commonBaseService.formatNumber(currentValue, { addCurrencySymbol: true, decimalPlaces });
	overallPnl = commonBaseService.formatNumber(overallPnl, { addSign: true, addCurrencySymbol: true, decimalPlaces });
	todaysPnl = commonBaseService.formatNumber(todaysPnl, { addSign: true, addCurrencySymbol: true, decimalPlaces });
	overallPnlPercentage = ltpFeedService.formatOneDayChangePercViewValue(overallPnlPercentage, decimalPlaces);
	netchangefromprevclose = ltpFeedService.formatOneDayChangePercViewValue(netchangefromprevclose, decimalPlaces);
	last_trade_price = ltpFeedService.formatLtpViewValue(last_trade_price, divider);
	one_day_change = ltpFeedService.formatOneDayChangeViewValue(one_day_change, decimalPlaces);
	return {
		currentValue,
		overallPnl,
		todaysPnl,
		overallPnlPercentage,
		last_trade_price,
		one_day_change,
		netchangefromprevclose,
		overallPnlColor,
		todaysPnlColor,
		oneDayChangeColor,
		netChangeColor,
	};
};

const formatRealtimePositionFeedValues = (feedValues = {}) => {
	let { netchangefromprevclose, last_trade_price, one_day_change, overallPnl, todaysPnl } = feedValues;
	const { divider, isCurrencyScrip } = feedValues;
	const overallPnlColor = equityBaseService.getEquityPriceColorBasedOnValue(overallPnl);
	const todaysPnlColor = equityBaseService.getEquityPriceColorBasedOnValue(todaysPnl);
	const oneDayChangeColor = equityBaseService.getEquityPriceColorBasedOnValue(one_day_change);
	const netChangeColor = equityBaseService.getEquityPriceColorBasedOnValue(netchangefromprevclose);
	netchangefromprevclose = ltpFeedService.formatOneDayChangePercViewValue(netchangefromprevclose);
	last_trade_price = ltpFeedService.formatLtpViewValue(last_trade_price, divider, false, isCurrencyScrip);
	one_day_change = ltpFeedService.formatOneDayChangeViewValue(one_day_change);
	overallPnl = commonBaseService.formatNumber(overallPnl, { addSign: true, addCurrencySymbol: true });
	todaysPnl = commonBaseService.formatNumber(todaysPnl, { addSign: true, addCurrencySymbol: true });
	return {
		last_trade_price,
		one_day_change,
		netchangefromprevclose,
		overallPnl,
		todaysPnl,
		overallPnlColor,
		todaysPnlColor,
		oneDayChangeColor,
		netChangeColor,
	};
};


const formatPositionDetails = (params = {}) => {
	const { overall_net_quantity, product_type, exchange, overall_sell_avg_price, overall_buy_avg_price, instrument } =
		params;
	let averagePrice = stocksPortfolioService.getAveragePrice(
		overall_net_quantity,
		overall_buy_avg_price,
		overall_sell_avg_price,
	);
	const netQuantity = commonBaseService.formatNumber(overall_net_quantity, { decimalPlaces: 0 });
	const decimalPlaces = ltpFeedService.getSupportedDecimals(instrument);
	averagePrice = commonBaseService.getNumberInDecimalFormat(averagePrice, decimalPlaces);
	return {
		productType: stocksPortfolioService.getProductTypeLabel(product_type, instrument),
		exchange: EXCHANGE_LABEL_MAPPER[exchange],
		netQuantity: netQuantity,
		averagePrice,
		instrument: instrument,
	};
};


const _formatBuyRowData = (avg_buy_price, buy_quantity, decimalPlaces, lot_size, instrument) => {
	const averageBuyPrice = avg_buy_price;
	const buyQuantity = buy_quantity;
	const averageValue = commonBaseService.getNumberInDecimalFormat(averageBuyPrice, decimalPlaces);
	const quantity = commonBaseService.formatNumber(buyQuantity, { decimalPlaces: 0 });
	let totalValue = stocksPortfolioPnlService.calculateInvestedValue(averageBuyPrice, buyQuantity, lot_size, instrument);
	totalValue = commonBaseService.formatNumber(totalValue, { decimalPlaces: 0 });
	return [
		{ title: POSITION_DETAILS.POSITION_BREAKUP_CARD.today_buy.title, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.orderValue.dataAid },
		{ title: quantity, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.qtyValue.dataAid },
		{ title: averageValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.avg_price_value.dataAid },
		{ title: totalValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.amount_value.dataAid },
	];
};

const _formatSellValues = (value) => {
	return `${value > 0 ? '-' : ''}${value}`;
};

const _formatSellRowData = (avg_sell_price, sell_quantity, decimalPlaces, lot_size, instrument) => {
	const averageSellPrice = avg_sell_price;
	const sellQuantity = _formatSellValues(sell_quantity);
	const averageValue = commonBaseService.getNumberInDecimalFormat(averageSellPrice, decimalPlaces);
	let totalValue = stocksPortfolioPnlService.calculateInvestedValue(
		averageSellPrice,
		sellQuantity,
		lot_size,
		instrument,
	);
	totalValue = commonBaseService.isValidNumber(totalValue) ? totalValue : 0;
	const quantity = commonBaseService.formatNumber(sellQuantity, { decimalPlaces: 0 });
	totalValue = commonBaseService.formatNumber(totalValue, { decimalPlaces: 0 });
	return [
		{ title: POSITION_DETAILS.POSITION_BREAKUP_CARD.today_sell.title, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.orderValue.dataAid },
		{ title: quantity, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.qtyValue.dataAid },
		{ title: averageValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.avg_price_value.dataAid },
		{ title: totalValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.amount_value.dataAid },
	];
};

const _formatCarryforwardRowData = (cf_avg_price, cf_quantity, decimalPlaces, lot_size, instrument) => {
	const averageValue = commonBaseService.getNumberInDecimalFormat(cf_avg_price, decimalPlaces);
	let totalValue = stocksPortfolioPnlService.calculateInvestedValue(cf_avg_price, cf_quantity, lot_size, instrument);
	totalValue = commonBaseService.isValidNumber(totalValue) ? totalValue : 0;
	const quantity = commonBaseService.formatNumber(cf_quantity, { decimalPlaces: 0 });
	totalValue = commonBaseService.formatNumber(totalValue, { decimalPlaces: 0 });
	return [
		{ title: POSITION_DETAILS.POSITION_BREAKUP_CARD.carryforward.title, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.orderValue.dataAid },
		{ title: quantity, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.qtyValue.dataAid },
		{ title: averageValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.avg_price_value.dataAid },
		{ title: totalValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.amount_value.dataAid },
	];
};

const _formatNetRowData = (net_quantity, avg_buy_price, avg_sell_price, decimalPlaces, lot_size, instrument) => {
	const netAvgPrice = stocksPortfolioService.getAveragePrice(net_quantity, avg_buy_price, avg_sell_price);
	const averageValue = commonBaseService.getNumberInDecimalFormat(netAvgPrice, decimalPlaces);
	const quantity = commonBaseService.formatNumber(net_quantity, { decimalPlaces: 0 });
	let totalValue = stocksPortfolioPnlService.calculateInvestedValue(netAvgPrice, net_quantity, lot_size, instrument);
	totalValue = commonBaseService.formatNumber(totalValue, { decimalPlaces: 0 });
	return [
		{ title: POSITION_DETAILS.POSITION_BREAKUP_CARD.net.title, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.orderValue.dataAid },
		{ title: quantity, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.qtyValue.dataAid },
		{ title: averageValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.avg_price_value.dataAid },
		{ title: totalValue, dataAid: POSITION_DETAILS.POSITION_BREAKUP_CARD.amount_value.dataAid },
	];
};

const formatPositionBreakupdata = (params = {}, decimalPlaces = 2) => {
	const {
		avg_buy_price,
		avg_sell_price,
		buy_quantity = '-',
		sell_quantity,
		cf_avg_price,
		cf_quantity,
		overall_net_quantity,
		overall_buy_avg_price,
		overall_sell_avg_price,
		lot_size,
		instrument,
	} = params;
	const buyRowData = _formatBuyRowData(avg_buy_price, buy_quantity, decimalPlaces, lot_size, instrument);
	const sellRowData = _formatSellRowData(avg_sell_price, sell_quantity, decimalPlaces, lot_size, instrument);
	const carryForwardRowData = _formatCarryforwardRowData(
		cf_avg_price,
		cf_quantity,
		decimalPlaces,
		lot_size,
		instrument,
	);
	const netRowData = _formatNetRowData(
		overall_net_quantity,
		overall_buy_avg_price,
		overall_sell_avg_price,
		decimalPlaces,
		lot_size,
		instrument,
	);
	return { breakupTabelData: [buyRowData, sellRowData, carryForwardRowData], breakupTotalData: [...netRowData] };
};


const stocksPortfolioFormatterService =  {
    constructHoldingsParamsForBatchRequest,
    constructPositionsParamsForBatchRequest,
    getFormattedHoldingsViewData,
	getFormattedPositionsViewData,
	getFormattedHoldingsListItemViewData,
	getFormattedPositionsListItemViewData,
    getFormattedExpiryDate,
    formatHoldingAndPositionCount,
	formatHoldingDetailsData,
	formatExchangeBottomsheetOptions,
	formatRealtimePnlValues,
	formatRealtimePositionFeedValues,
	formatPositionDetails,
	formatPositionBreakupdata
};

export default stocksPortfolioFormatterService;
